import React from 'react';
import MentimeterPollDisplay from '../components/MentimeterPollDisplay.jsx';
import HostCheckboxPreview from '../components/HostCheckboxPreview.jsx';
import HostOrderPreview from '../components/HostOrderPreview.jsx';
import HostMatchPreview from '../components/HostMatchPreview.jsx';
import HostClassifyPreview from '../components/HostClassifyPreview.jsx';
import HostExprPreview from '../components/HostExprPreview.jsx';
import HostNumericPreview from '../components/HostNumericPreview.jsx';
import CorrectnessTally from '../components/CorrectnessTally.jsx';
import { MC_STYLES } from '../constants/mcStyles.js';
import '../styles/host.css';

const KIND_LABEL = {
  poll: 'ความคิดเห็น',
  mc: 'คำถามปรนัย',
  checkbox_sim: 'ลองทำจริง',
  order: 'เรียงลำดับ',
  match: 'จับคู่ลำดับ',
  classify: 'เลือกชนิดข้อมูล',
  expr: 'เติมตัวดำเนินการ',
  numeric: 'ตอบเป็นตัวเลข',
};

/**
 * The projected view of one question — used both as a pre-open preview and
 * as the live view once the teacher opens it. `question` is the server's
 * teacher-side shape ({type, payload, tally, revealed, answeredCount,
 * totalPlayers}); pass `phase` = 'preview' | 'open' | 'revealed'.
 */
export default function ProjectorQuestion({ question, objective, phase, answered, total, clock = null }) {
  const text =
    question.payload?.question || question.payload?.instruction || question.payload?.heading || '';

  return (
    <div className="pq" data-phase={phase}>
      <div className="pq-head">
        {objective?.reflection ? (
          <span className={`objective-chip ${objective.reflection ? 'is-reflection' : ''}`}>
            <span>{objective.label}</span>
            <span className="pq-objective-text">{objective.text}</span>
          </span>
        ) : (
          <span className="q-kicker">{KIND_LABEL[question.type] || 'คำถาม'}</span>
        )}
      </div>

      {text && <h1 className="pq-question">{text}</h1>}
      {clock ? (
        // Kahoot's middle row: the clock on the left, the answers so far on
        // the right, and whatever the question shows (its code) between.
        <div className="pq-clock-row">
          {clock.left}
          <div className="pq-clock-mid">
            {question.payload?.code && <pre className="q-code pq-code">{question.payload.code}</pre>}
          </div>
          {clock.right}
        </div>
      ) : (
        question.payload?.code && <pre className="q-code pq-code">{question.payload.code}</pre>
      )}

      <div className="pq-body">
        <TallyDisplay question={question} />
      </div>

      {phase === 'open' && question.type !== 'poll' && (
        <div className="pq-progress" aria-hidden="true">
          <div
            className="pq-progress-fill"
            style={{ width: `${Math.round(((answered ?? 0) / Math.max(1, total ?? 1)) * 100)}%` }}
          />
          <span className="pq-progress-label num">
            ตอบแล้ว {answered ?? 0}/{total ?? 0}
          </span>
        </div>
      )}
      {phase === 'preview' && (
        <p className="pq-hint text-dim">กด “เปิดให้ตอบ” เมื่อพร้อมให้ผู้เรียนส่งคำตอบ</p>
      )}

      {phase === 'revealed' && question.fastestCorrect && (
        <p className="pq-fastest">
          <span aria-hidden="true">⚡</span> ตอบถูกเร็วที่สุด&nbsp;
          <strong>{question.fastestCorrect.nickname}</strong>
          &nbsp;·&nbsp;<span className="num">{(question.fastestCorrect.responseMs / 1000).toFixed(1)} วิ</span>
          &nbsp;·&nbsp;<span className="num">+{question.fastestCorrect.points}</span>
        </p>
      )}
    </div>
  );
}

function TallyDisplay({ question }) {
  const { type, payload, tally, revealed } = question;

  if (!revealed) {
    if (type === 'mc' && payload?.options) {
      return (
        <div className="host-live-options-grid">
          {payload.options.map((t, i) => {
            const style = MC_STYLES[i % MC_STYLES.length];
            return (
              <div key={i} className="host-live-option-card" style={{ '--option-color': `var(${style.colorVar})` }}>
                <span className="host-live-option-shape" aria-hidden="true">{style.shape}</span>
                <span className="host-live-option-text">{t}</span>
              </div>
            );
          })}
        </div>
      );
    }
    if (type === 'poll') return <MentimeterPollDisplay tally={tally} answeredCount={question.answeredCount ?? 0} />;
    if (type === 'checkbox_sim' && payload?.checkboxes) return <HostCheckboxPreview payload={payload} revealed={false} tally={tally} />;
    if (type === 'order' && payload?.cards) return <HostOrderPreview payload={payload} revealed={false} tally={tally} questionId={question.id} />;
    if (type === 'match' && payload?.slots) return <HostMatchPreview payload={payload} revealed={false} tally={tally} />;
    if (type === 'classify' && payload?.items) return <HostClassifyPreview payload={payload} revealed={false} tally={tally} />;
    if (type === 'expr' && payload?.items) return <HostExprPreview payload={payload} revealed={false} tally={tally} />;
    if (type === 'numeric' && payload?.expression) return <HostNumericPreview payload={payload} revealed={false} tally={tally} />;
    return null;
  }

  if (type === 'poll') return <MentimeterPollDisplay tally={tally} answeredCount={question.answeredCount ?? 0} />;

  if (type === 'mc' && tally?.kind === 'counts') {
    const totalVotes = tally.counts.reduce((a, b) => a + b, 0) || 1;
    return (
      <div className="tally-bars">
        {payload.options.map((t, i) => {
          const style = MC_STYLES[i % MC_STYLES.length];
          const count = tally.counts[i] || 0;
          const pct = Math.round((count / totalVotes) * 100);
          const isCorrect = payload.correctIndex === i;
          return (
            <div key={i} className={`tally-bar-row ${isCorrect ? 'is-correct' : ''}`}>
              <div className="tally-bar-label">
                <span className="tally-bullet" style={{ background: `var(${style.colorVar})` }} />
                <span className="tally-text">{t}</span>
                {isCorrect && <span className="correct-badge">ถูกต้อง</span>}
              </div>
              <div className="tally-bar-track">
                <div className="tally-bar-fill" style={{ width: `${pct}%`, background: `var(${style.colorVar})` }} />
              </div>
              <span className="tally-bar-count">{count} คน</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (type === 'checkbox_sim' && payload?.checkboxes) return <HostCheckboxPreview payload={payload} revealed tally={tally} />;
  if (type === 'order' && payload?.cards) return <HostOrderPreview payload={payload} revealed tally={tally} questionId={question.id} />;
  if (type === 'match' && payload?.slots) return <HostMatchPreview payload={payload} revealed tally={tally} />;
  if (type === 'classify' && payload?.items) return <HostClassifyPreview payload={payload} revealed tally={tally} />;
  if (type === 'expr' && payload?.items) return <HostExprPreview payload={payload} revealed tally={tally} />;
  if (type === 'numeric' && payload?.expression) return <HostNumericPreview payload={payload} revealed tally={tally} />;
  if (tally?.kind === 'correctness') return <CorrectnessTally tally={tally} />;
  return null;
}
